import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, Tooltip, Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import DepartmentHeader from '../components/DepartmentHeader';
import { useDepartment } from '../context/DepartmentContext';
import { getSubjects, createSubject, updateSubject, deleteSubject, getSemesters } from '../services/api';

const Subjects = () => {
  const { selectedDepartment, departments } = useDepartment();
  const [subjects, setSubjects] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentSubject, setCurrentSubject] = useState({
    subject_name: '',
    subject_code: '',
    credits: '',
    department_id: '',
    semester_id: '',
    is_lab: false,
    schedule_consecutive: false,
    preferred_day_id: '',
    schedule_common: false,
    batch_wise: false,
    consecutive_sections: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Update currentSubject when selectedDepartment changes
  useEffect(() => {
    if (selectedDepartment) {
      setCurrentSubject(prev => ({
        ...prev,
        department_id: selectedDepartment.department_id
      }));
    }
  }, [selectedDepartment]);

  const columns = [
    { field: 'subject_id', headerName: 'ID', width: 70 },
    { field: 'subject_name', headerName: 'Subject Name', flex: 1 },
    { field: 'subject_code', headerName: 'Subject Code', width: 150 },
    { field: 'credits', headerName: 'Credits', width: 100 },
    { field: 'semester_name', headerName: 'Semester', width: 150 },
    { 
      field: 'is_lab', 
      headerName: 'Lab', 
      width: 100,
      renderCell: (params) => (
        params.row.is_lab ? 
          <Chip size="small" label="LAB" color="secondary" /> : 
          null
      )
    },
    { field: 'schedule_consecutive', headerName: 'Consecutive', width: 120,
      renderCell: (params) => (
        params.row.schedule_consecutive ? <Chip size="small" label="Yes" color="primary"/> : null
      )
    },
    { field: 'schedule_common', headerName: 'Common', width: 110,
      renderCell: (params) => (
        params.row.schedule_common ? <Chip size="small" label="Yes" color="success"/> : null
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleOpenDialog(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.subject_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDepartment) return;
      
      try {
        setLoading(true);
        const [subjectsResponse, semestersResponse] = await Promise.all([
          getSubjects(),
          getSemesters()
        ]);
        
        // Filter subjects by department
        const departmentSubjects = subjectsResponse.data.filter(
          subject => subject.department_id === selectedDepartment.department_id
        );
        
        setSubjects(departmentSubjects);
        setSemesters(semestersResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDepartment]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = (subject = null) => {
    if (subject) {
      setCurrentSubject({
        subject_name: subject.subject_name,
        subject_code: subject.subject_code,
        credits: subject.credits,
        department_id: selectedDepartment.department_id,
        semester_id: subject.semester_id || '',
        is_lab: subject.is_lab || false,
        schedule_consecutive: subject.schedule_consecutive || false,
        preferred_day_id: subject.preferred_day_id || '',
        schedule_common: subject.schedule_common || false,
        batch_wise: subject.batch_wise || false,
        consecutive_sections: subject.consecutive_sections || ''
      });
      setSelectedId(subject.subject_id);
    } else {
      setCurrentSubject({
        subject_name: '',
        subject_code: '',
        credits: '',
        department_id: selectedDepartment.department_id,
        semester_id: '',
        is_lab: false,
        schedule_consecutive: false,
        preferred_day_id: '',
        schedule_common: false,
        batch_wise: false,
        consecutive_sections: ''
      });
      setSelectedId(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteSubject(selectedId);
      setSubjects(subjects.filter(subject => subject.subject_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Subject deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting subject:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete subject',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs (like is_lab)
    const fieldValue = type === 'checkbox' ? checked : value;
    
    // Toggle preferred_day_id reset if schedule_consecutive unchecked
    if(name === 'schedule_consecutive' && !fieldValue){
        setCurrentSubject(prev => ({...prev, schedule_consecutive: false, preferred_day_id: ''}));
    }else{
        setCurrentSubject(prev => ({ ...prev, [name]: fieldValue }));
    }
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentSubject.subject_name.trim()) {
      errors.subject_name = 'Subject name is required';
    }
    if (!currentSubject.subject_code.trim()) {
      errors.subject_code = 'Subject code is required';
    }
    if (!currentSubject.credits) {
      errors.credits = 'Credits is required';
    } else if (isNaN(currentSubject.credits) || currentSubject.credits <= 0) {
      errors.credits = 'Credits must be a positive number';
    }
    if(currentSubject.schedule_consecutive && !currentSubject.preferred_day_id){
      errors.preferred_day_id = 'Select preferred day';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const subjectData = {
        ...currentSubject,
        credits: parseInt(currentSubject.credits, 10),
        department_id: selectedDepartment.department_id,
        schedule_consecutive: currentSubject.schedule_consecutive,
        preferred_day_id: currentSubject.schedule_consecutive ? currentSubject.preferred_day_id : null,
        schedule_common: currentSubject.schedule_common,
        batch_wise: currentSubject.batch_wise,
        consecutive_sections: currentSubject.consecutive_sections
      };

      if (selectedId) {
        // Update existing subject
        await updateSubject(selectedId, subjectData);
        
        // Find semester name for display
        const semesterName = subjectData.semester_id
          ? semesters.find(sem => sem.semester_id === parseInt(subjectData.semester_id))?.semester_name
          : null;
        
        setSubjects(subjects.map(subject => 
          subject.subject_id === selectedId 
            ? { 
                ...subject, 
                ...subjectData, 
                subject_id: selectedId,
                semester_name: semesterName
              } 
            : subject
        ));
        
        setSnackbar({
          open: true,
          message: 'Subject updated successfully',
          severity: 'success'
        });
      } else {
        // Create new subject
        const response = await createSubject(subjectData);
        
        // Find semester name for display
        const semesterName = subjectData.semester_id
          ? semesters.find(sem => sem.semester_id === parseInt(subjectData.semester_id))?.semester_name
          : null;
        
        const newSubject = {
          ...subjectData,
          subject_id: response.data.subject_id,
          semester_name: semesterName
        };
        
        setSubjects([...subjects, newSubject]);
        
        setSnackbar({
          open: true,
          message: 'Subject created successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving subject:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save subject',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <PageHeader 
        title="Subjects Management" 
        subtitle="Add and manage subjects for courses" 
      />
      
      {/* Department Header */}
      {selectedDepartment && (
        <DepartmentHeader returnPath="/subjects" />
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Subject
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={subjects}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            getRowId={(row) => row.subject_id}
            autoHeight
            disableSelectionOnClick
            sx={{ minHeight: 400 }}
          />
        )}
      </Paper>

      {/* Add/Edit Subject Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              name="subject_name"
              label="Subject Name"
              fullWidth
              margin="normal"
              value={currentSubject.subject_name}
              onChange={handleInputChange}
              error={!!formErrors.subject_name}
              helperText={formErrors.subject_name}
            />
            <TextField
              name="subject_code"
              label="Subject Code"
              fullWidth
              margin="normal"
              value={currentSubject.subject_code}
              onChange={handleInputChange}
              error={!!formErrors.subject_code}
              helperText={formErrors.subject_code}
            />
            <TextField
              name="credits"
              label="Credits"
              type="number"
              fullWidth
              margin="normal"
              value={currentSubject.credits}
              onChange={handleInputChange}
              error={!!formErrors.credits}
              helperText={formErrors.credits}
              inputProps={{ min: 1 }}
            />

            <FormControl fullWidth margin="normal" disabled>
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={currentSubject.department_id}
                label="Department"
                onChange={handleInputChange}
              >
                <MenuItem value={selectedDepartment?.department_id}>
                  {selectedDepartment?.department_name}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Semester</InputLabel>
              <Select
                name="semester_id"
                value={currentSubject.semester_id}
                label="Semester"
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {semesters
                  .filter(semester => !currentSubject.department_id || semester.department_id === parseInt(currentSubject.department_id))
                  .map((semester) => {
                    // Find department name for this semester
                    const departmentName = departments.find(dept => dept.department_id === semester.department_id)?.department_name || 'Unknown';
                    return (
                      <MenuItem key={semester.semester_id} value={semester.semester_id}>
                        {semester.semester_name} ({semester.academic_year}) - {departmentName}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Tooltip title="Mark this subject as a laboratory session (2-hour session)">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_lab"
                      checked={!!currentSubject.is_lab}
                      onChange={handleInputChange}
                      color="secondary"
                    />
                  }
                  label="This is a Lab Subject (2-hour session)"
                />
              </Tooltip>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentSubject.schedule_consecutive}
                    onChange={handleInputChange}
                    name="schedule_consecutive"
                  />
                }
                label="Schedule Consecutively"
              />
            </Box>

            {currentSubject.schedule_consecutive && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Preferred Day</InputLabel>
                <Select
                  name="preferred_day_id"
                  value={currentSubject.preferred_day_id}
                  label="Preferred Day"
                  onChange={handleInputChange}
                >
                  <MenuItem value={1}>Monday</MenuItem>
                  <MenuItem value={2}>Tuesday</MenuItem>
                  <MenuItem value={3}>Wednesday</MenuItem>
                  <MenuItem value={4}>Thursday</MenuItem>
                  <MenuItem value={5}>Friday</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={currentSubject.schedule_common} onChange={handleInputChange} name="schedule_common"/>}
                label="Schedule Simultaneously in All Sections (Common)"/>
              </Box>

            {currentSubject.is_lab && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentSubject.batch_wise}
                    onChange={(e) => setCurrentSubject({ ...currentSubject, batch_wise: e.target.checked })}
                  />
                }
                label="Split lab into batches"
              />
            )}

            {currentSubject.schedule_consecutive && (
              <TextField
                name="consecutive_sections"
                label="Sections for Consecutive Block (e.g., A,B,C)"
                fullWidth
                margin="normal"
                value={currentSubject.consecutive_sections}
                onChange={handleInputChange}
                helperText="Leave blank to apply to all sections"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this subject? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Subjects;
